import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { UAParser } from 'ua-parser-js';
import { CLIs, Crawlers, Emails, Fetchers } from 'ua-parser-js/extensions';

import { gengql } from '@/__generated__';
import { UserCapabilities } from '@/__generated__/graphql';
import { DocumentNodeResult } from '@/utils/gql';

// @ts-ignore - This is a hack to make the code compile
const uaParser = new UAParser([CLIs, Crawlers, Emails, Fetchers]);

interface BillingAccount {
  id: string;
  type?: string;
  state?: string;
  isSMBAccount?: boolean;
  default_card?: {
    brand?: string;
    last4digits?: string;
  };
}

/** @deprecated Use UserCapabilities from @/__generated__/graphql */
export type GroupCapabilities = Omit<UserCapabilities, '__typename'>;

export interface Group {
  id: string;
  name: string;
  /**
   * Whether this is an internal OnFrontiers team. Already selected by
   * `GET_VIEWER` below; typed here so `useIsInternalStaff` (ENG-5684) can read
   * it. `User.groups` searches with MemberOnly — an active `team_members` row —
   * and passes no internal filter, so an internal team the viewer actually
   * belongs to arrives here, which is what makes it an honest staff signal.
   */
  internal?: boolean;
  billing_account: BillingAccount;
  account_type: 'basic' | 'advanced' | 'enterprise';
  capabilities: GroupCapabilities;
  default_anonymous_messaging: boolean;
}

const GET_VIEWER = gengql(/* GraphQL */ `
  query getViewer($userId: String) {
    user(id: $userId) {
      id
      billing_account_id
      admin
      expert_state
      expert_stats {
        consultation_count
        candidate_summary {
          matched_count
          verified_count
          vetting_count
        }
      }
      compliance_completed_at
      signup_type
      has_password
      locked
      signup_subdomain
      password_expiry {
        expiry
        expired
        expiring
      }
      agreements {
        policy
        updated_at
        accepted
      }
      capabilities {
        chat
        internalNetwork
        activityLogs
        keywordDefinitions
        archivedConsultations
        privateDisclosure
      }
      groups {
        id
        name
        slug
        html_url
        branding_logo_url
        branding_show_poweredbyof
        billing_account {
          id
          type
          state
          isSMBAccount
          display_currency
          default_card {
            brand
            last4digits
          }
          credit_balance {
            cents
            currency
          }
        }
        about
        account_type
        capabilities {
          chat
          internalNetwork
          activityLogs
          keywordDefinitions
          archivedConsultations
          privateDisclosure
          planType
        }
        internal
        internal_network {
          id
          name
        }
        default_anonymous_messaging
      }
      profile {
        id
        first_name
        last_name
        name
        html_url
        url_endpoint
        linkedin_username
        linkedin_url
        title
        summary
        skype
        timezone
        city
        country
        picture_url
        questions
        cv_url
        available_long_term
        available_marketplace
        hide_profile
        bill_rate
        credit_rate
        client_rate
        expert_state
        languages {
          code
          fluency
        }
        keywords
        tier
      }
      # deprecated fields
      first_name
      last_name
      name
      picture_url
      country_code
      timezone
      username
      phone
      html_url
      email {
        address
        accepted
        confirmed
        verified
      }
      pendingTeamMembership {
        id
        teamId
        teamName
      }
    }
  }
`);

/**
 * The consulting-engagements gate, deliberately NOT part of GET_VIEWER (ENG-5715).
 *
 * Both fields are new, and an unknown field is a GraphQL *validation* error: it
 * rejects the whole document before execution, so `errorPolicy` cannot salvage
 * it. GET_VIEWER's rejection is not a dark feature — `fetchViewer` throws,
 * `index.tsx` catches, and that catch calls `logout()`. A webapp build reaching
 * an environment ahead of the api, or an api rollback after this ships, would
 * therefore log out every signed-in user rather than hide a button.
 *
 * `live/session.ts` already documents the same mechanism for `admin`. Keeping the
 * gate in its own query means the worst case is the answer both fields already
 * default to: off.
 *
 * This does not make the whole feature deploy-order-free — the consultation and
 * project documents select `engagements_enabled` inline, and those pages do fail
 * if the api is behind. It buys the difference between a broken page and a
 * signed-out user, which is the difference worth paying a round trip for.
 */
const GET_ENGAGEMENT_GATES = gengql(/* GraphQL */ `
  query getEngagementGates($userId: String) {
    engagementsAvailable
    user(id: $userId) {
      id
      groups {
        id
        engagements_enabled
      }
    }
  }
`);

export type Viewer = NonNullable<DocumentNodeResult<typeof GET_VIEWER>['user']> & {
  full_name?: string;
  intercomHash?: string;
  userAgentParsed: ReturnType<typeof uaParser.getResult>;
  /**
   * Whether consulting engagements exist in this environment at all (ENG-5715).
   *
   * NOT an account answer - a deployment with the service on still has it false
   * for every account that has not been onboarded - so it bounds the staff and
   * expert overrides in `canViewEngagementSurfaces` and nothing else.
   */
  engagementsAvailable: boolean;
  /**
   * Which of the viewer's groups have the product, by id (ENG-5715).
   *
   * An id list rather than a field on each group because it arrives from a
   * different document than `groups` does - see GET_ENGAGEMENT_GATES above - and
   * merging it back onto them would let a caller read a gate answer off a group
   * that never carried one. Surfaces that hold their OWN group (a consultation,
   * a project) read `engagements_enabled` off it directly instead; this list is
   * only for the viewer's active "view as" account.
   *
   * Empty means off, which is also what a failed or refused gate query leaves.
   */
  engagementsEnabledGroupIds: string[];
};

export async function fetchViewer(client: ApolloClient<NormalizedCacheObject>): Promise<Viewer> {
  // Issued together, not in sequence: GET_ENGAGEMENT_GATES takes no variable from
  // the viewer result — `$userId` is optional and the api resolves the caller —
  // so serialising them would have bought nothing but a second round trip before
  // first paint.
  const [{ data }, gates] = await Promise.all([
    client.query({ query: GET_VIEWER }),
    fetchEngagementGates(client),
  ]);
  const viewer = { ...data.user } as Viewer;
  viewer.full_name = viewer.profile?.name || undefined;
  viewer.userAgentParsed = uaParser.getResult();
  Object.assign(viewer, gates);
  return viewer;
}

/**
 * The gate query, swallowed on any failure.
 *
 * Awaited before first paint rather than fired off, because every gate is a
 * synchronous predicate over the store and an answer landing later would flicker
 * surfaces on and off. Awaited alongside GET_VIEWER rather than after it, so the
 * wait is the slower of the two rather than their sum.
 *
 * The catch is the whole point (see GET_ENGAGEMENT_GATES): this must never be
 * able to reject `fetchViewer`, because that path logs the user out. Off is the
 * safe default and the value every consumer already treats as "no feature".
 */
async function fetchEngagementGates(
  client: ApolloClient<NormalizedCacheObject>
): Promise<Pick<Viewer, 'engagementsAvailable' | 'engagementsEnabledGroupIds'>> {
  try {
    const { data } = await client.query({ query: GET_ENGAGEMENT_GATES });
    return {
      // `?? false` for the same reason its sibling below carries `?? []`: the
      // field is typed non-optional, and a partial payload should not make the
      // type a lie. Behaviour is unchanged either way, since `!undefined` denies.
      engagementsAvailable: data.engagementsAvailable ?? false,
      engagementsEnabledGroupIds: (data.user?.groups ?? [])
        .filter((g) => g?.engagements_enabled)
        .map((g) => g.id),
    };
  } catch (error) {
    // Reported, not silent. Off is the right default, but a transient 5xx, a cold
    // start, or genuine api/webapp flag drift is otherwise indistinguishable from
    // "this account is not onboarded" — the feature darks for the whole session
    // and nothing says why.
    //
    // `level: 'warning'`, and the same reasoning as `useGovernanceApprover.ts`,
    // which captures its identical fail-closed error because "an environment whose
    // flags have drifted apart looks exactly like one where nobody governs". The
    // viewer is not broken and nothing was lost.
    Sentry.captureException(error, {
      level: 'warning',
      tags: { engagement_gate_query_failed: 'true' },
    });
    return { engagementsAvailable: false, engagementsEnabledGroupIds: [] };
  }
}

export const loggedOutViewer: Viewer = {
  id: '',
  html_url: '',
  admin: false,
  capabilities: {
    chat: false,
    internalNetwork: false,
    activityLogs: false,
    keywordDefinitions: false,
    archivedConsultations: false,
    privateDisclosure: false,
  },
  groups: [],
  agreements: [],
  engagementsAvailable: false,
  engagementsEnabledGroupIds: [],
  userAgentParsed: uaParser.getResult(),
};

// Harness viewer
export type CurrentUserType = {
  OF_ID: string | null;
  EMAIL: string | null;
  NAME: string | null;
  BROWSER: string | null;
  LOCATION: string | null;
};

export const CurrentUser: CurrentUserType = {
  OF_ID: null,
  EMAIL: null,
  NAME: null,
  BROWSER: null,
  LOCATION: null,
};
